<script setup lang="ts">
import type { ResolvableScript } from "@unhead/vue"
import { VueQueryDevtools } from "@tanstack/vue-query-devtools"
import initPendo from "~/analytics/pendo"

const { init } = useAuth()
const route = useRoute<"product">()

const productContent = useProductContentStore()
const productVariants = useProductVariantsStore()

await productVariants.waitForProducts()

const site = useSiteConfig()

const { pendo } = useRuntimeConfig().public

usePwaCache()

const licences = useLicences()

onMounted(async () => {
  const gaHash = await init()
  if (licences.isFetching.value) {
    await licences.suspense()
  }
  initPendo(pendo.apiKey, gaHash)
})

const bodyBgClass = computed(() => {
  if (productContent.activeArticle?.video) {
    return "bg-[--theme-dark]"
  } else if (productContent.activeArticle) {
    return "bg-platform-paper"
  } else if (productVariants.activeVariant) {
    return "bg-[--theme-darkest]"
  } else if (productVariants.activeProduct) {
    return "bg-[--theme-lightest]"
  } else {
    return "bg-platform-paper"
  }
})

const previewMode = usePreviewMode()

const { $parseStringToDocument } = useNuxtApp()

useActiveProductSeoMeta()

// https://unhead.unjs.io/
// you can use this to manipulate the document head in any components,
useHead({
  title: computed(() => {
    const title =
      route.meta.title ||
      productContent.activeArticle?.title ||
      productContent.activeSubject?.title ||
      "Bestå eksamen lettere"

    const productTitle = productVariants.activeProduct?.productTitle || route.params.product?.replaceAll("-", " ")

    const product = productTitle ? [site.name, capitalize(productTitle)].filter(distinctAndTruthy).join(" ") : null

    return [title, product].filter(distinctAndTruthy).join(" - ")
  }),
  meta: [
    {
      name: "description",
      content: () => {
        if (productVariants.activeProduct?.header) {
          const plainText = $parseStringToDocument(productVariants.activeProduct.header).body.textContent
          if (plainText) {
            return plainText
          }
        }

        if (typeof route.meta.description === "string" && route.meta.description) {
          return route.meta.description
        }

        return site.description
      },
    },
  ],
  link: [{ rel: "icon", type: "image/x-icon" }],
  bodyAttrs: {
    "data-theme": computed(
      () => productVariants.activeProduct?.theme ?? "kunne",
    ),
    class: computed(() => `${bodyBgClass.value} min-h-screen m-0`),
  },
  script: computed<ResolvableScript[]>(() => {
    const scripts: ResolvableScript[] = []

    if (previewMode.enabled.value) {
      scripts.push({
        src: "https://app.crystallize.com/static/frontend-preview-listener.js",
        defer: true,
      })
    }

    if (site.env === "production") {
      scripts.push(
        {
          src: "https://js-eu1.hs-scripts.com/25807489.js",
          id: "hs-script-loader",
          async: true,
          defer: true,
        },
        {
          src: "https://cdn-cookieyes.com/client_data/4b9a2b0d9cdf9d97e17edfe6/script.js",
        },
      )
    }

    return scripts
  }),
})
</script>

<template>
  <NuxtPwaAssets />
  <UMaintenanceBanner />
  <UDeviceLimiter />
  <UToast />
  <NuxtLayout>
    <NuxtPage />
  </NuxtLayout>
  <UPayResult />
  <UAudioPlayer />
  <VueQueryDevtools />
</template>

<style lang="css" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
